import { Map as MapLibreMap, NavigationControl, GeolocateControl, ScaleControl, AttributionControl } from 'maplibre-gl';
import { createRadar } from './map/radar.js';
import { createMeteoPanel } from './ui/meteoPanel.js';
import { createRelief3d } from './map/relief3d.js';
import { buildStyle } from './map/style.js';
import { createFilterPanel } from './ui/filterPanel.js';
import { createFavoritesPanel } from './ui/favoritesPanel.js';
import { createRecents } from './ui/recents.js';
import { createTracksPanel } from './ui/tracksPanel.js';
import { createRoutePanel } from './ui/routePanel.js';
import { createNavOverlay } from './ui/navOverlay.js';
import { createSearchBox } from './ui/searchBox.js';
import { createAccount } from './ui/account.js';
import { createInfoPopup } from './map/popup.js';
import { backend } from './data/backend.js';
import { toast } from './ui/toast.js';
import { ortungVorbereiten, zurueckTaste } from './data/native.js';
import { BASIS } from './data/basis.js';
import './styles/app.css';

const DEFAULT_VIEW = { center: [8.2275, 46.8], zoom: 7.4 };

/**
 * Soweit die Karte traegt - weiter nicht.
 *
 * Die Ausdehnung stammt aus der Reliefquelle selbst (tiles.json von
 * ch.swisstopo.relief.vt: 3.57..13.66 Ost, 44.18..48.88 Nord). Jenseits davon
 * gibt es keine Schummerung mehr, und ohne sie sieht die Karte aus, als waere
 * etwas kaputt. Lieber gar nicht erst hinlassen.
 */
const AUSDEHNUNG = [[3.57, 44.18], [13.66, 48.88]];

/* ------------------------------------------------------- Ansicht in der URL */

const LETZTE_ANSICHT = 'kgva.ansicht';

/**
 * Was ein Link mitbringen kann: `#map=z/lat/lng`, dazu `ort=lat,lng,Name` für
 * einen Punkt mit Auskunft und `route=mittel;lat,lng,Name;lat,lng,Name` für eine
 * Route. Der eigentliche Zustand bleibt `map=` - `ort` und `route` werden beim
 * Öffnen einmal ausgeführt und verschwinden mit der nächsten Kartenbewegung.
 */
function hashLesen() {
  const teile = new URLSearchParams(location.hash.replace(/^#/, ''));
  const m = /^([\d.]+)\/(-?[\d.]+)\/(-?[\d.]+)$/.exec(teile.get('map') || '');
  const punkt = (text) => {
    const [lat, lng, ...name] = String(text || '').split(',');
    const p = { lat: Number(lat), lng: Number(lng), name: name.join(',') };
    return Number.isFinite(p.lat) && Number.isFinite(p.lng) ? p : null;
  };
  // Roh aufteilen, erst dann dekodieren: ein `;` im Namen steht im Link als %3B und
  // darf die Routenpunkte nicht auseinanderreissen.
  const roh = /(?:^|&)route=([^&]*)/.exec(location.hash.replace(/^#/, ''))?.[1] || '';
  const entschluesseln = (t) => {
    try { return decodeURIComponent(t || ''); } catch { return ''; }
  };
  const [mittel, von, nach] = roh.split(';').map(entschluesseln);
  return {
    view: m ? { center: [Number(m[3]), Number(m[2])], zoom: Number(m[1]) } : null,
    ort: punkt(teile.get('ort')),
    route: mittel && punkt(von) && punkt(nach) ? { mode: mittel, from: punkt(von), to: punkt(nach) } : null
  };
}

function writeHash(map) {
  const c = map.getCenter();
  const hash = `#map=${map.getZoom().toFixed(2)}/${c.lat.toFixed(5)}/${c.lng.toFixed(5)}`;
  history.replaceState(null, '', hash);
  // Beim nächsten Start hier weitermachen. Die App hat keine Adresszeile, und
  // jedes Öffnen begann sonst wieder bei der ganzen Schweiz.
  try {
    localStorage.setItem(LETZTE_ANSICHT, JSON.stringify({ center: [c.lng, c.lat], zoom: map.getZoom() }));
  } catch { /* privater Modus */ }
}

function letzteAnsicht() {
  try {
    const v = JSON.parse(localStorage.getItem(LETZTE_ANSICHT));
    return Number.isFinite(v?.zoom) && Number.isFinite(v?.center?.[0]) ? v : null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------- Karte */

/** Ohne WebGL zeichnet MapLibre nichts - das soll man erfahren, nicht vermuten. */
if (!document.createElement('canvas').getContext('webgl2')
  && !document.createElement('canvas').getContext('webgl')) {
  document.getElementById('map').innerHTML =
    '<p class="map__fehler">Dieser Browser kann die Karte nicht zeichnen: WebGL fehlt oder ist abgeschaltet.</p>';
  throw new Error('WebGL fehlt');
}

const beimStart = hashLesen();
const view = beimStart.view || letzteAnsicht() || DEFAULT_VIEW;

const map = new MapLibreMap({
  container: 'map',
  style: buildStyle(),
  center: view.center,
  zoom: view.zoom,
  maxZoom: 19,
  maxBounds: AUSDEHNUNG,
  hash: false,
  attributionControl: false,
  dragRotate: false,
  pitchWithRotate: false,
  // Telefone melden das Dreifache. Das sind mehr als doppelt so viele Bildpunkte
  // wie beim Zweifachen, für einen Unterschied, den man bei Karten kaum sieht -
  // aber im Akku und beim Verschieben sehr wohl.
  pixelRatio: Math.min(window.devicePixelRatio || 1, 2)
});

/* Zoom und Standort oben rechts, am Rand der Karte - dort liegt der Daumen
   nicht ueber dem, was man gerade ansieht. Unten links die Herkunft der
   Daten, der Massstab eine Zeile darueber. Der Kompass erscheint nur, wenn die
   Karte gedreht oder gekippt ist: dann ist er der Weg zurück. */
map.addControl(new NavigationControl({ showCompass: true, visualizePitch: true }), 'top-right');
const standort = new GeolocateControl({ trackUserLocation: true, positionOptions: { enableHighAccuracy: true } });
map.addControl(standort, 'top-right');
map.addControl(new ScaleControl({ maxWidth: 110, unit: 'metric' }), 'bottom-left');
// Schmal zugeklappt: dort nähme die Zeile sonst zwei Reihen über der Karte ein.
// Über das (i) bleibt sie erreichbar, wie es die Lizenzen verlangen.
const quellenKlein = window.matchMedia('(max-width: 860px), (max-height: 500px)').matches;
map.addControl(new AttributionControl({ compact: quellenKlein }), 'bottom-left');
if (quellenKlein) {
  map.once('load', () => {
    const q = map.getContainer().querySelector('.maplibregl-ctrl-attrib');
    q?.classList.remove('maplibregl-compact-show');
    q?.removeAttribute('open');
  });
}

const schraegMelden = () => {
  map.getContainer().classList.toggle('ist-schraeg', map.getBearing() !== 0 || map.getPitch() !== 0);
};
map.on('rotate', schraegMelden);
map.on('pitch', schraegMelden);

map.on('moveend', () => writeHash(map));

// Gleich hier abwarten: `load` feuert nur einmal, und wer sich später anmeldet und
// `loaded()` fragt, kann genau danebenliegen, solange noch Kacheln nachkommen.
const karteBereit = new Promise((fertig) => map.once('load', fertig));

// Im Entwicklungsmodus greifbar machen - erleichtert das Nachsehen in der Konsole
if (import.meta.env?.DEV) window.map = map;

/* ---------------------------------------------------------- Kopfzeile / UI */

const el = (id) => document.getElementById(id);

const panel = el('panel');
const backdrop = el('panel-backdrop');
const sidebar = el('sidebar');
const sidebarBtn = el('btn-sidebar');
const infoTafel = el('info');

/** Schmales Fenster: das Panel liegt über der Karte statt daneben. */
const compact = () => window.matchMedia('(max-width: 860px), (max-height: 500px)').matches;
/**
 * Telefon und Tablet hochkant: alles Bedienbare unten, die Karte darüber.
 *
 * Nach der Höhe gefragt, nicht nach `orientation`: die Bildschirmtastatur verkleinert
 * das Fenster, und auf einem Tablet wäre es beim Tippen plötzlich „quer“ - das Blatt
 * spränge mitten in der Eingabe an die Seite. Dieselbe Bedingung steht in app.css.
 */
const UNTEN = '(max-width: 560px), (max-width: 860px) and (min-height: 440px)';
const untenAngeordnet = () => window.matchMedia(UNTEN).matches;

/* Rechts wird bedient, links wird eingestellt.
 *
 * Die Schiene am rechten Rand steht immer; daneben faehrt die Spalte aus, in
 * der genau eines liegt: Auskunft, Route oder Favoriten. Die Darstellung -
 * Hintergrund, Ebenen, Punkte - oeffnet links und ist zu, solange niemand
 * danach fragt. So gehoert die Mitte der Karte.
 */
const filterBtn = el('btn-filters');
const routeBtn = el('btn-route');
const favBtn = el('btn-favorites');
const tourBtn = el('btn-tracks');
const meteoBtn = el('btn-meteo');
const railButtons = [routeBtn, favBtn, tourBtn, meteoBtn, filterBtn];

/** Wird weiter unten gesetzt; die Knoepfe laufen erst nach dem Aufbau. */
let info = null;
let panelMelden = null;

function setPanel(open) {
  // Unten ist nur für ein Blatt Platz, sonst bliebe von der Karte ein Streifen.
  if (open && untenAngeordnet()) info?.schliessen();
  panel.hidden = !open;
  backdrop.hidden = !open || !compact();
  filterBtn.classList.toggle('is-active', open);
  filterBtn.setAttribute('aria-pressed', String(open));
  panelMelden?.(open);
  map.resize();
}

function setSidebar(open) {
  sidebar.classList.toggle('is-zu', !open);
  sidebarBtn.setAttribute('aria-pressed', String(open));
  map.resize();
}

/** Nach einer Auswahl soll auf dem Telefon wieder die Karte zu sehen sein. */
function collapseOnCompact() {
  if (compact()) setPanel(false);
}

/** Was gerade in der rechten Spalte steht - oder nichts. */
const offeneAnsicht = () => {
  if (infoTafel.hidden) return null;
  if (!el('view-route').hidden) return 'route';
  if (!el('view-favorites').hidden) return 'favoriten';
  if (!el('view-tracks').hidden) return 'touren';
  if (!el('view-meteo').hidden) return 'meteo';
  return 'auskunft';
};

function showView(name) {
  if (name === 'filters') {
    setPanel(panel.hidden);
    return;
  }
  // Nochmals auf denselben Knopf: die Spalte wieder zu. Nur nicht bei den
  // Touren, solange aufgezeichnet wird - die Zahlen sollen stehen bleiben.
  const jetzt = offeneAnsicht();
  collapseOnCompact();
  if ((name === 'route' && jetzt === 'route')
    || (name === 'favorites' && jetzt === 'favoriten')
    || (name === 'tracks' && jetzt === 'touren' && !tracks.laeuft())
    || (name === 'meteo' && jetzt === 'meteo')) {
    info?.schliessen();
    return;
  }
  if (name === 'route') info?.routeZeigen();
  else if (name === 'favorites') info?.favoritenZeigen();
  else if (name === 'tracks') info?.tourenZeigen();
  else if (name === 'meteo') {
    info?.meteoZeigen();
    // Beim ersten Mal den nächstgelegenen Prognosepunkt nehmen - eine leere
    // Spalte mit einem Suchfeld sagt niemandem, was dahintersteckt.
    ortsprognose.zeigen();
  }
}

for (const b of railButtons) {
  b.addEventListener('click', () => showView(b.dataset.view));
}
sidebarBtn.addEventListener('click', () => setSidebar(sidebar.classList.contains('is-zu')));

// Die Darstellung bleibt zu, bis jemand sie aufmacht - die Karte zuerst.
setPanel(false);
setSidebar(true);

backdrop.addEventListener('click', () => setPanel(false));
el('panel-close').addEventListener('click', () => setPanel(false));
map.on('click', collapseOnCompact);

/**
 * Teilen: die Ansicht, und wenn es sie gibt, auch die Route oder den Punkt.
 *
 * In der App zeigt `location` auf https://localhost - so ein Link führt bei
 * niemandem irgendwohin. Dort steht die öffentliche Adresse in der Datenbasis.
 */
function linkBauen({ ort = null, routeDaten = null } = {}) {
  const c = map.getCenter();
  const wurzel = BASIS ? `${BASIS}/` : `${location.origin}${location.pathname}`;
  const punkt = (p) => `${p.lat.toFixed(5)},${p.lng.toFixed(5)},${encodeURIComponent(p.name || '')}`;
  let hash = `map=${map.getZoom().toFixed(2)}/${c.lat.toFixed(5)}/${c.lng.toFixed(5)}`;
  if (ort) hash += `&ort=${punkt(ort)}`;
  else if (routeDaten) hash += `&route=${routeDaten.mode};${punkt(routeDaten.from)};${punkt(routeDaten.to)}`;
  return `${wurzel}#${hash}`;
}

async function teilen({ ort = null } = {}) {
  const routeDaten = ort ? null : route.fuerLink();
  const url = linkBauen({ ort, routeDaten });
  const was = ort ? 'Link zu diesem Punkt' : (routeDaten ? 'Link zur Route' : 'Link zur Ansicht');
  // Auf dem Telefon das Teilen-Blatt des Systems; am Rechner wäre es ein Umweg.
  if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
    try {
      await navigator.share({ title: ort?.name || 'maps@kgva.ch', url });
      return;
    } catch (e) {
      if (e.name === 'AbortError') return;
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    toast(`${was} kopiert.`);
  } catch {
    toast(url, 8000);
  }
}
el('btn-share').addEventListener('click', () => teilen());

standort.on('error', (e) => {
  toast(e.code === 1
    ? 'Ortung ist nicht erlaubt. Sie lässt sich in den Einstellungen des Browsers freigeben.'
    : 'Standort gerade nicht verfügbar.', 4000);
});
standort.on('outofmaxbounds', () => toast('Der Standort liegt ausserhalb der Karte.', 3000));

window.addEventListener('offline', () => toast('Keine Verbindung. Geladene Kartenteile bleiben sichtbar.', 4000));
// Wieder Netz: was unterwegs entstanden ist, hochladen und die Listen auffrischen.
window.addEventListener('online', async () => {
  const p = account.getProfile();
  if (p?.server && await backend.nachholen(p).catch(() => false)) {
    favorites.load();
    tracks.load();
  }
});

/* --------------------------------------------------- Konto, Favoriten, Filter */

const account = createAccount({
  button: el('btn-account'),
  // Touren hängen wie die Favoriten am Profil - beim Wechsel blieben sonst die
  // des vorherigen in der Liste stehen.
  onChange: () => {
    favorites.load();
    tracks.load();
    loadPrefs();
  }
});

const favorites = createFavoritesPanel({
  map,
  host: el('fav-list'),
  badge: el('fav-count'),
  modeEl: el('fav-mode'),
  backend,
  getProfile: () => account.getProfile(),
  onNavigate: collapseOnCompact,
  // Ein Favorit zeigt, was man beim ersten Mal auch gesehen hat.
  onZeigen: (f) => info?.zeigeTreffer({ name: f.name, lat: f.lat, lng: f.lng, art: f.kind }),
  // Eine gespeicherte Reise fuehrt nicht zu einem Punkt, sondern zurueck in
  // die Routenplanung.
  onReise: (reise) => {
    route.restore(reise);
    // Nicht `showView`: das Setzen der Punkte schlägt die Route schon auf, und ein
    // zweites Aufschlagen derselben Ansicht wäre der Klick, der sie wieder schliesst.
    info?.routeZeigen();
    collapseOnCompact();
  }
});

/**
 * Palettenwechsel lädt den Style neu. `diff: true` hält die Kacheln im Speicher,
 * aber Sichtbarkeiten und eigene Ebenen (Route) sind danach weg - beides wird
 * hier wieder aufgetragen.
 */
let renderedPalette = 'farbig';
function usePalette(palette) {
  if (!palette || palette === renderedPalette) return;
  renderedPalette = palette;
  map.setStyle(buildStyle({ palette }), { diff: true });
  map.once('styledata', () => {
    filters.reapply();
    route.redraw();
    tracks.redraw();
    search.redraw();
    radar.reapply();
    relief3d.reapply();
  });
}

const filters = createFilterPanel({
  map,
  onChange: () => savePrefsSoon(),
  onPalette: usePalette,
  // "Alle zeigen" im Filterfeld räumt auch das Suchfeld auf.
  onKategorieWeg: () => search?.leeren()
});
panelMelden = (open) => filters.setSichtbar(open);

// Zielfuehrung blickt schraeg nach vorn - ohne Gelaende wirkt das wie eine
// umgelegte Tapete. Danach den vorherigen Zustand wiederherstellen.
let dreiDVorher = false;
let panelVorher = false;
const navOverlay = createNavOverlay({
  map,
  onStart: () => {
    dreiDVorher = relief3d.istAn();
    relief3d.setOn(true, { stillLassen: true });
  },
  onEnd: () => {
    relief3d.setOn(dreiDVorher, { stillLassen: true });
    // Wie es vorher war - nicht einfach offen: wer die Darstellung gar nicht
    // offen hatte, bekam sie nach jeder Fahrt ungefragt aufgeschlagen.
    setPanel(panelVorher);
  }
});

const route = createRoutePanel({
  map,
  // Immer aufschlagen, nie umschalten: hier wird gerade ein Punkt gesetzt.
  onActivate: () => info?.routeZeigen(),
  onStartNavigation: (auftrag) => {
    panelVorher = !panel.hidden;
    setPanel(false);
    navOverlay.starten(auftrag);
  },
  /**
   * Eine Reise liegt bei den Favoriten - dieselbe Ablage, derselbe Weg zum
   * Server. `lat`/`lng` sind der Start, damit die Marke in der Karte steht;
   * unter `reise` liegt alles, was die Routenplanung zum Wiederaufnehmen
   * braucht.
   */
  onSaveTrip: (reise) => favorites.add({
    name: `${reise.from.name || 'Start'} → ${reise.to.name || 'Ziel'}`,
    kind: 'Reise',
    lat: reise.from.lat,
    lng: reise.from.lng,
    reise
  })
});
/**
 * Das Niederschlagsradar liegt jetzt unter einem eigenen Schienen-Eintrag.
 * Laeuft es, steht das auch dann am Knopf, wenn die Spalte etwas anderes zeigt
 * - sonst sucht man den Regen in den Einstellungen.
 */
const radar = createRadar({
  map,
  onZustand: (an) => {
    const marke = el('meteo-state');
    if (marke) marke.hidden = !an;
  }
});
/**
 * Die Ortsprognose steht unter dem Radar in derselben Spalte: Radar sagt, wo
 * es gerade regnet, die Prognose, wie die Woche wird.
 */
const ortsprognose = createMeteoPanel({ map });
const relief3d = createRelief3d({ map });

// Beim Setzen eines Routenpunkts aus der Karte heraus nicht sofort zuklappen -
// die Liste der Verbindungen ist ja das Ergebnis.

let prefsTimer;
function savePrefsSoon() {
  clearTimeout(prefsTimer);
  prefsTimer = setTimeout(async () => {
    try {
      await backend.prefs.save(account.getProfile(), { filters: filters.getState() });
    } catch { /* Einstellungen sind nicht kritisch */ }
  }, 600);
}

async function loadPrefs() {
  try {
    const prefs = await backend.prefs.load(account.getProfile());
    if (prefs?.filters) filters.setState(prefs.filters);
  } catch { /* Standardfilter behalten */ }
}

radar.mount(el('radar'));
ortsprognose.mount(el('ortsprognose'));
relief3d.mount(el('relief3d'));

filters.mount({
  basemapHost: el('basemap'),
  groupHost: el('layer-groups'),
  themeHost: el('poi-themes'),
  nameInput: el('poi-name'),
  resetBtn: el('btn-reset'),
  poiNoneBtn: el('btn-poi-none')
});

route.mount({
  modeHost: el('route-modes'),
  fromInput: el('route-from'),
  toInput: el('route-to'),
  swapBtn: el('route-swap'),
  hereFrom: el('route-here-from'),
  hereTo: el('route-here-to'),
  clearBtn: el('route-clear'),
  result: el('route-result'),
  whenBox: el('route-when'),
  dateInput: el('route-date'),
  timeInput: el('route-time'),
  depBtn: el('route-dep'),
  arrBtn: el('route-arr'),
  nowBtn: el('route-now'),
  veloBox: el('route-velo'),
  veloZusammenfassung: el('route-velo-zeile'),
  hint: el('route-hint'),
  veloTyp: el('route-velotyp'),
  veloHuegel: el('route-velo-huegel'),
  veloVerkehr: el('route-velo-verkehr'),
  veloBelag: el('route-velo-belag')
});

/**
 * Touren: aufzeichnen, wiedersehen, als GPX weitergeben.
 */
const tracks = createTracksPanel({
  map,
  backend,
  getProfile: () => account.getProfile(),
  onAktiv: () => info?.tourenZeigen(),
  onNavigate: collapseOnCompact
});
tracks.mount({
  aufnahme: el('tour-rec'),
  liste: el('tour-list'),
  badge: el('tour-count'),
  importBtn: el('tour-import'),
  importInput: el('tour-file')
});

/**
 * Zuletzt angesehen - die Liste unten in der Schiene. Ein Klick darauf fliegt
 * hin und schlaegt dieselbe Auskunft wieder auf.
 */
const recents = createRecents({
  host: el('recent-list'),
  onPick: (ort) => {
    map.flyTo({ center: [ort.lng, ort.lat], zoom: Math.max(map.getZoom(), 15), duration: 700 });
    info?.zeigeTreffer(ort);
    collapseOnCompact();
  }
});

info = createInfoPopup({
  map,
  favorit: {
    ist: (p) => favorites.istFavorit(p),
    umschalten: (p, fav) => favorites.umschalten(p, fav)
  },
  onTeilen: (ort) => teilen({ ort }),
  onRoutePoint: (which, point) => route.setPoint(which, point),
  onBesucht: (ort) => recents.merken(ort),
  /* Der Knopf in der Schiene leuchtet, solange seine Ansicht offen ist - und
     kommt Inhalt, faehrt die Spalte auf, auch wenn sie eingeklappt war. */
  onAnsicht: (welche) => {
    for (const [knopf, name] of [[routeBtn, 'route'], [favBtn, 'favoriten'], [tourBtn, 'touren'], [meteoBtn, 'meteo']]) {
      knopf.classList.toggle('is-active', welche === name);
      knopf.setAttribute('aria-pressed', String(welche === name));
    }
    if (welche) setSidebar(true);
  }
});

const search = createSearchBox({
  map,
  input: el('search-input'),
  clearBtn: el('search-clear'),
  resultList: el('search-results'),
  // Ein Suchtreffer soll nicht nur anfliegen, sondern auch gleich erzaehlen,
  // was er ist - Marke in der Karte, Auskunft daneben.
  onPick: (treffer) => {
    collapseOnCompact();
    info?.zeigeTreffer(treffer);
  },
  onKategorie: (kat) => filters.setKategorie(kat),
  zuletzt: () => recents.liste()
});

/** Was ein Link mitgebracht hat: einmal ausführen, sobald die Karte steht. */
function linkAusfuehren({ ort, route: linkRoute }) {
  if (linkRoute) route.restore(linkRoute);
  else if (ort) info?.zeigeTreffer({ name: ort.name, lat: ort.lat, lng: ort.lng });
}

window.addEventListener('hashchange', () => {
  const h = hashLesen();
  if (h.view) map.jumpTo(h.view);
  linkAusfuehren(h);
});

(async () => {
  // `restore` meldet das Profil und lädt damit auch Favoriten, Touren und
  // Einstellungen - hier nicht ein zweites Mal.
  await account.restore();
  karteBereit.then(() => linkAusfuehren(beimStart));
  // Hat das System die letzte Aufzeichnung abgewürgt, ist sie hier wieder da.
  await tracks.sicherungPruefen();
  // In der App einmal die Ortung freischalten. Im Browser tut das nichts -
  // dort fragt der Browser selbst, sobald jemand den Standortknopf drückt.
  await ortungVorbereiten();
})();

map.on('error', (e) => {
  // Reliefkacheln decken nur die Schweiz ab – fehlende Kacheln sind normal.
  const url = e?.error?.url || '';
  if (url.includes('wmts.geo.admin.ch')) return;
  console.warn('Kartenfehler:', e?.error?.message || e);
});

const tipptGerade = () => {
  const a = document.activeElement;
  return a?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(a?.tagName);
};

document.addEventListener('keydown', (e) => {
  if (e.key === '/' && !tipptGerade()) {
    e.preventDefault();
    el('search-input').focus();
  }
});

// Auswahlgruppen: Pfeiltasten springen weiter, wie man es von role="radio" erwartet.
document.addEventListener('keydown', (e) => {
  const schritt = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
  const gruppe = e.target.closest?.('[role="radiogroup"]');
  if (!schritt || !gruppe) return;
  const alle = [...gruppe.querySelectorAll('[role="radio"]')];
  const neu = alle[(alle.indexOf(e.target) + schritt + alle.length) % alle.length];
  if (!neu) return;
  e.preventDefault();
  neu.focus();
  neu.click();
});

// Auf schmalen Geräten trägt das Suchfeld nicht den ganzen Satz.
const suchtextLang = el('search-input').placeholder;
const anordnungMelden = () => {
  el('search-input').placeholder = untenAngeordnet() ? 'Suchen …' : suchtextLang;
  backdrop.hidden = panel.hidden || !compact();
  // Quer dürfen Darstellung und Spalte beide offen sein, hochkant nicht: gedreht
  // blieb von der Karte sonst nichts übrig, und die Leiste rutschte aus dem Bild.
  if (untenAngeordnet() && !panel.hidden && offeneAnsicht()) setPanel(false);
};
anordnungMelden();
window.matchMedia(UNTEN).addEventListener('change', anordnungMelden);

/**
 * Eine Lage zurück.
 *
 * Die Reihenfolge ist die, in der die Dinge übereinander liegen: zuerst die
 * Trefferliste der Suche, dann die Darstellungsspalte, dann die Seitenspalte
 * mit Route, Favoriten, Touren, Wetter oder Auskunft. Erst wenn davon nichts
 * offen ist, gibt `false` zurück und der Aufrufer entscheidet - in der App
 * heisst das: in den Hintergrund, nicht beenden.
 *
 * Die Zielführung steht über allem und wird nicht beiläufig beendet. Ein
 * Druck kündigt es an, der zweite führt es aus: unterwegs ist die
 * Zurück-Geste schnell einmal ausgelöst, und eine abgebrochene Führung
 * mitten auf der Strasse ist ärgerlicher als ein zusätzlicher Druck.
 */
let fuehrungAbbruchBis = 0;

function eineLageZurueck() {
  if (navOverlay.laeuft()) {
    if (Date.now() < fuehrungAbbruchBis) {
      fuehrungAbbruchBis = 0;
      navOverlay.beenden();
    } else {
      fuehrungAbbruchBis = Date.now() + 3000;
      toast('Nochmals zurück beendet die Führung.');
    }
    return true;
  }
  if (!el('search-results').hidden) {
    search.hide();
    return true;
  }
  if (!panel.hidden) {
    setPanel(false);
    return true;
  }
  if (offeneAnsicht()) {
    info?.schliessen();
    return true;
  }
  return false;
}

zurueckTaste(eineLageZurueck);
// Wie bei `window.map`: im Dev-Bau greifbar, damit sich die Abfolge ohne
// Android-Gerät prüfen lässt.
if (import.meta.env?.DEV) window.zurueck = eineLageZurueck;

// Dieselbe Regel für die Esc-Taste: eine Lage je Druck. Bisher hörten mehrere
// Stellen getrennt zu, und ein Esc im Suchfeld schloss auch die Spalte dahinter.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape' || document.querySelector('dialog[open]')) return;
  // Die Führung beendet man nicht aus Versehen mit einer Taste.
  if (navOverlay.laeuft()) return;
  eineLageZurueck();
});
